import React, { useRef, useEffect } from "react";
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { List, ListItem } from "@mui/joy";
import { Error as ErrorIcon } from "@mui/icons-material";
import { isValidEvent, formatTimestamp, formatExecutionTimeNatural } from "./utils";

const EventLogsCard = ({ events }) => {
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [events]);

  return (
    <Card variant="outlined" sx={{ px: 3, mt: 3 }}>
      <CardContent>
        <Typography level="h3" gutterBottom>
          Event Logs
        </Typography>

        <List
          ref={listRef}
          sx={{
            mx: 'calc(-1 * var(--ModalDialog-padding))',
            px: 'var(--ModalDialog-padding)',
            maxHeight: 400,
            overflowY: 'auto',
            pr: 1,
            position: 'relative',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(150, 150, 150, 0.6)',
              borderRadius: '6px',
              transition: 'background 0.3s ease-in-out, opacity 0.3s',
              opacity: 0,
            },
            '&:hover::-webkit-scrollbar-thumb': {
              opacity: 1,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(150, 150, 150, 0.9)',
            },
          }}
        >
          {Array.isArray(events) && events.length > 0 ? (
            events.every(isValidEvent) ? (
              events.map((event, index) => (
                <ListItem key={index}>
                  <Stack spacing={0.5} sx={{ p: 1, borderRadius: '8px', bgcolor: 'background.level1', width: '100%' }}>
                    <Typography level="body2">
                      <strong>[{formatTimestamp(event.timestamp)}]</strong>
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="sm"
                        color={
                          event.level === 'error'
                            ? 'danger'
                            : event.level === 'warning'
                              ? 'warning'
                              : 'success'
                        }
                      >
                        {event.level.toUpperCase()}
                      </Chip>
                      <Typography level="body2">{formatExecutionTimeNatural(event.log)}</Typography>
                    </Stack>
                  </Stack>
                </ListItem>
              ))
            ) : (
              (() => {
                const firstInvalid = events.find(e => !isValidEvent(e));
                return (
                  <Box sx={{ textAlign: 'center', color: 'text.primary', p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', my: 2 }}>
                    <ErrorIcon sx={{ fontSize: 32, mb: 1, color: 'danger.500' }} />
                    <Typography level="body1" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                      Malformed event log data
                    </Typography>
                    <Typography level="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      The event logs could not be displayed because the data format is invalid or incomplete.<br />
                    </Typography>
                    {firstInvalid && (
                      <Box sx={{ mt: 2, textAlign: 'left', bgcolor: 'background.level2', p: 2, borderRadius: 1, overflowX: 'auto', color: 'text.primary' }}>
                        <Typography level="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'text.primary' }}>
                          Example of malformed event:
                        </Typography>
                        <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'inherit' }}>
                          {JSON.stringify(firstInvalid, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </Box>
                );
              })()
            )
          ) : (
            <Typography level="body2" sx={{ textAlign: 'center', color: 'neutral' }}>
              No logs available.
            </Typography>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export default EventLogsCard;
